<template>
  <div class="login-step">
    <div class="step-header">
      <button class="back-btn" @click="$emit('go-back')" t_label="btn_emp2_Back_PasswordStep">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M12 4L6 10L12 16" stroke="rgba(0,0,0,0.8)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <line x1="6" y1="10" x2="16" y2="10" stroke="rgba(0,0,0,0.8)" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </button>
      <h1 class="step-title">{{ $t('lbl_emp3_EnterPassword') }}</h1>
    </div>

    <!-- 按照原型图：Logging into、Password输入框、Forgot Password? 在一个容器内，gap: 16px -->
    <div class="password-form-container">
      <!-- 用户名显示文本 -->
      <div class="password-username-display">
        <span>{{ $t('lbl_emp2_LoggingInto') }} </span>
        <strong>{{ loginIdentifier }}</strong>
      </div>
      
      <!-- 密码输入框 -->
      <div class="input-wrapper">
        <div class="password-input-wrapper">
          <div class="input-container-placeholder" :class="{ 'input-error': passwordError }">
            <!-- 浮动标签（有值或错误时显示在边框上方） -->
            <label v-if="passwordError || password" class="input-label" :class="{ 'label-error': passwordError }">
              {{ $t('lbl_emp2_Password') }} <span class="required">*</span>
            </label>
            <!-- 占位符（无值时显示在输入框内部） -->
            <span v-if="!password && !passwordError" class="input-placeholder">
              {{ $t('lbl_emp2_Password') }} <span class="required">*</span>
            </span>
            <input
              ref="passwordInput"
              :value="password"
              :type="showPassword ? 'text' : 'password'"
              class="form-input-placeholder"
              :class="{ 'input-error': passwordError }"
              placeholder=""
              @focus="$emit('clear-password-error')"
              @input="handleInput"
            />
          </div>
          <button
            type="button"
            class="password-toggle"
            @click="$emit('toggle-password')"
            t_label="btn_emp2_TogglePassword_PasswordStep"
          >
            <svg v-if="!showPassword" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="rgba(0,0,0,0.6)" stroke-width="2"/>
              <line x1="1" y1="1" x2="23" y2="23" stroke="rgba(0,0,0,0.6)" stroke-width="2"/>
            </svg>
            <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="rgba(0,0,0,0.6)" stroke-width="2"/>
              <circle cx="12" cy="12" r="3" stroke="rgba(0,0,0,0.6)" stroke-width="2"/>
            </svg>
          </button>
        </div>
        <!-- 错误信息 -->
        <div v-if="passwordError" class="password-error-message">
          {{ $t('lbl_emp2_InvalidPassword') }}
        </div>
      </div>
      
      <!-- Forgot Password? 链接 -->
      <div class="forgot-link-wrapper">
        <a href="#" @click.prevent="$emit('show-forgot-password')" class="forgot-link">{{ $t('lbl_emp2_ForgotPasswordLink') }}</a>
      </div>
    </div>

    <button
      class="btn-primary"
      @click="$emit('login')"
      :disabled="!password || loading"
      t_label="btn_emp2_Login"
    >
      {{ $t('lbl_emp2_Login') }}
    </button>

    <slot name="divider">
      <div class="divider">
        <span>{{ $t('lbl_emp2_OR') }}</span>
      </div>
    </slot>

    <!-- 流程2（email/mobile登录）：显示"Send code to log in"按钮 -->
    <slot v-if="loginMethod === 'email' || loginMethod === 'mobile'" name="alternative-login">
      <div class="alternative-login">
        <button class="btn-secondary" @click="$emit('switch-login', 'otp')" t_label="btn_emp2_LoginByOTP_FromPassword">
          {{ loginMethod === 'email' ? $t('lbl_emp2_LoginByVerificationCodeEmail', { emailInput: email }) : $t('lbl_emp2_LoginByVerificationCodeMobile', { countryCode: countryCodePlain, mobileNumber: mobileNumber }) }}
        </button>
      </div>
    </slot>

    <!-- 流程1（username登录）：显示"Login with Mobile"、"Login with Email"和SSO图标 -->
    <slot v-else name="alternative-login-username">
      <div class="alternative-login">
        <button class="btn-secondary btn-with-indicator" @click="$emit('switch-login', 'mobile')" t_label="btn_emp2_LoginWithMobile_FromPasswordStep">
          {{ $t('lbl_emp2_LoginWithMobile') }}
        </button>
        <button class="btn-secondary btn-with-indicator" @click="$emit('switch-login', 'email')" t_label="btn_emp2_LoginWithEmail_FromPasswordStep">
          {{ $t('lbl_emp2_LoginWithEmail') }}
        </button>
        <!-- SSO 登录暂时隐藏 -->
        <!-- <div class="sso-login">
          <button class="sso-btn wechat" @click="$emit('wechat-login')" title="Login with WeChat">
            <img src="https://cdn.adsalecdn.com/EMP/Backend/Images/wechat_icon.png" alt="WeChat" />
          </button>
          <button class="sso-btn google" @click="$emit('google-login')" title="Login with Google">
            <img src="https://cdn.adsalecdn.com/EMP/Backend/Images/google_icon.png" alt="Google" />
          </button>
          <button class="sso-btn linkedin" @click="$emit('linkedin-login')" title="Login with LinkedIn">
            <img src="https://cdn.adsalecdn.com/EMP/Backend/Images/linkedin_icon.png" alt="LinkedIn" />
          </button>
        </div> -->
      </div>
    </slot>
  </div>
</template>

<script>
export default {
  name: 'PasswordStep',
  props: {
    password: {
      type: String,
      default: ''
    },
    passwordError: {
      type: Boolean,
      default: false
    },
    showPassword: {
      type: Boolean,
      default: false
    },
    loginIdentifier: {
      type: String,
      required: true
    },
    loginMethod: {
      type: String,
      default: 'username'
    },
    email: {
      type: String,
      default: ''
    },
    mobileNumber: {
      type: String,
      default: ''
    },
    countryCode: {
      type: String,
      default: '+86'
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    countryCodePlain() {
      // 文案里已自带前缀“+”，这里去掉可能的“+”避免重复
      return String(this.countryCode || '').replace(/^\+/, '');
    }
  },
  mounted() {
    // 组件挂载后自动聚焦到密码输入框
    this.$nextTick(() => {
      if (this.$refs.passwordInput) {
        this.$refs.passwordInput.focus();
      }
    });
  },
  methods: {
    handleInput(event) {
      const value = event.target.value.replace(/\s/g, '');
      event.target.value = value;
      this.$emit('update:password', value);
    }
  }
};
</script>

<style scoped>
.login-step {
  gap: 16px !important;
}

.step-header {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
}

.back-btn {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
}

.back-btn:hover {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 50%;
}

.step-title {
  color: rgba(0, 0, 0, 0.80);
  font-size: 28px;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  line-height: 36.40px;
  margin: 0;
  word-wrap: break-word;
  flex: 1;
}

@media (max-width: 768px) {
  .step-title {
    font-size: 24px;
    line-height: 31.20px;
  }
}

.password-form-container {
  align-self: stretch;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 16px;
  display: flex;
}

.password-username-display {
  align-self: stretch;
  color: rgba(0, 0, 0, 0.60);
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  line-height: 18.20px;
  word-wrap: break-word;
}

.password-username-display strong {
  color: rgba(0, 0, 0, 0.80);
  font-weight: 600;
}

.input-wrapper {
  align-self: stretch;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 4px;
  display: flex;
  overflow: visible;
}

.password-input-wrapper {
  width: 100%;
  position: relative;
  display: flex;
  align-items: center;
}

.input-container-placeholder {
  width: 100%;
  position: relative;
  overflow: visible;
}

.input-container-placeholder.input-error {
  border-color: #CD1515 !important;
}

.input-label {
  position: absolute;
  left: 12px;
  top: -5px;
  padding: 0 4px;
  background: white;
  display: inline-flex;
  align-items: center;
  color: rgba(0, 0, 0, 0.60);
  font-size: 12px;
  font-family: 'Roboto', sans-serif;
  font-weight: 400;
  line-height: 12px;
  letter-spacing: 0.15px;
  white-space: nowrap;
  z-index: 5;
  pointer-events: none;
  margin: 0;
  transform: translateY(0);
}

.input-label.label-error {
  color: #D32F2F;
}

.input-placeholder {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: rgba(0, 0, 0, 0.60);
  font-size: 14px;
  font-family: 'Roboto', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.15px;
}

.form-input-placeholder {
  width: 100%;
  padding: 12px 40px 12px 12px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  background: #F8F7FF;
  font-size: 14px;
  font-family: 'Roboto', sans-serif;
  color: rgba(0, 0, 0, 0.80);
  transition: border-color 0.2s, background 0.2s;
}

.form-input-placeholder:focus {
  outline: none;
  border-color: #7366FF;
  background: white;
}

.form-input-placeholder.input-error {
  border-color: #CD1515 !important;
}

.password-toggle {
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  width: 24px;
  height: 24px;
}

.password-toggle svg {
  width: 24px;
  height: 24px;
}

.password-toggle:hover {
  opacity: 0.7;
}

.required {
  color: #CD1515;
  font-size: 14px;
  font-family: 'Roboto', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.15px;
  margin-left: 4px;
}

.password-error-message {
  padding-left: 14px;
  padding-right: 14px;
  color: #D32F2F !important;
  font-size: 12px;
  font-family: 'Roboto', sans-serif;
  font-weight: 400;
  letter-spacing: 0.40px;
  word-wrap: break-word;
  line-height: 20px;
}

.forgot-link-wrapper {
  align-self: stretch;
  justify-content: flex-start;
  align-items: center;
  gap: 0;
  display: inline-flex;
}

.forgot-link {
  color: rgba(0, 0, 0, 0.40) !important;
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  line-height: 14px;
  text-decoration: none !important;
}

.btn-primary {
  width: 100%;
  height: 42px !important;
  padding: 0 24px !important;
  background: #7366FF !important;
  color: white !important;
  border: none !important;
  border-radius: 999px !important;
  font-size: 14px !important;
  font-family: 'Inter', sans-serif !important;
  font-weight: 600 !important;
  line-height: 21px !important;
  cursor: pointer !important;
  transition: all 0.2s !important;
  box-sizing: border-box !important;
  display: inline-flex !important;
  justify-content: center !important;
  align-items: center !important;
}

.btn-primary:hover:not(:disabled) {
  background: #5a4dcc !important;
  color: white !important;
  border: none !important;
}

.btn-primary:disabled {
  opacity: 0.6 !important;
  cursor: not-allowed !important;
  background: #B8B3FF !important;
  color: rgba(255, 255, 255, 0.9) !important;
}

.divider {
  display: flex;
  align-items: center;
  gap: 24px;
  margin: 0;
}

.divider::before,
.divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: rgba(0, 0, 0, 0.10);
}

.divider span {
  color: rgba(0, 0, 0, 0.50);
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  line-height: 14px;
}

.alternative-login {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 0;
  padding: 0;
}

.btn-secondary {
  align-self: stretch;
  height: 42px;
  padding: 0 24px;
  border-radius: 999px;
  outline: 1px rgba(0, 0, 0, 0.60) solid;
  outline-offset: -1px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  display: inline-flex;
  background: white !important;
  background-color: white !important;
  border: none !important;
  cursor: pointer;
  transition: all 0.2s;
  color: rgba(0, 0, 0, 0.80);
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  line-height: 21px;
  word-wrap: break-word;
  box-sizing: border-box;
}

.btn-secondary:hover {
  background: rgba(0, 0, 0, 0.03) !important;
  background-color: rgba(0, 0, 0, 0.03) !important;
  outline-color: rgba(0, 0, 0, 0.60);
}

.sso-login {
  width: 100%;
  display: flex;
  gap: 10px;
  justify-content: center;
}

.sso-btn {
  flex: 1 1 0;
  height: 74px;
  background: rgba(0, 0, 0, 0.03);
  border: none;
  border-radius: 999px;
  position: relative;
  overflow: hidden;
  cursor: pointer;
}

.sso-btn:hover {
  background: rgba(0, 0, 0, 0.06);
}

.sso-btn svg,
.sso-btn img {
  width: 45px;
  height: 45px;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  object-fit: contain;
}
</style>

<!-- 全局样式覆盖 -->
<style>
/* 引入共享样式 */
@import '../Common/new-login-shared.css';

.alternative-login .btn-secondary {
  background: white !important;
  background-color: white !important;
  border: none !important;
  color: rgba(0, 0, 0, 0.80) !important;
}

.alternative-login .btn-secondary:hover {
  background: rgba(0, 0, 0, 0.03) !important;
  background-color: rgba(0, 0, 0, 0.03) !important;
  color: rgba(0, 0, 0, 0.80) !important;
}
</style>

